import React from 'react';
import { Link } from 'react-router-dom';
import LiveSearchBox from './LiveSearchBox';
import { CategoryIcon, ChevronIcon } from './icons';
import { HelpCategory, HelpSearchHit } from './helpdeskApi';

interface SearchViewProps {
    query: string;
    results: HelpSearchHit[];
    categories: HelpCategory[];
    isLoading: boolean;
}

/**
 * Dedicated search-results page — ported from `searchPage()` in
 * prototype/lib/templates.js. Shows result count + list when there is a
 * query, or a "browse a category" fallback (with a link to support) when
 * there are zero matches or no query yet.
 */
const SearchView: React.FC<SearchViewProps> = ({ query, results, categories, isLoading }) => {
    const trimmedQuery = query.trim();
    const hasQuery = trimmedQuery.length > 0;
    const hasResults = results.length > 0;

    return (
        <section className="hc-wrap hc-section hc-section--tight">
            <LiveSearchBox variant="page" initialValue={query} />

            {hasQuery && !isLoading && (
                <p className="hc-search-count">
                    {results.length} result{results.length === 1 ? '' : 's'} for <strong>{trimmedQuery}</strong>
                </p>
            )}

            {hasQuery && hasResults && (
                <div className="hc-results">
                    {results.map((hit) => (
                        <Link key={hit.url} className="hc-result" to={hit.url}>
                            <span className="hc-result__cat">{hit.categoryTitle}</span>
                            <span className="hc-result__title">{hit.title}</span>
                            <span className="hc-result__sum">{hit.summary}</span>
                        </Link>
                    ))}
                </div>
            )}

            {hasQuery && !hasResults && !isLoading && (
                <div className="hc-noresult">
                    <h2>No articles found for &ldquo;{trimmedQuery}&rdquo;.</h2>
                    <p>Try a different term, browse a category, or contact support.</p>
                    {categories.length > 0 && (
                        <div className="hc-cat-grid hc-cat-grid--compact">
                            {categories.slice(0, 6).map((cat) => (
                                <Link key={cat.slug} className="hc-cat-card" to={`/helpdesk/${cat.slug}`}>
                                    <span className="hc-cat-card__icon">
                                        <CategoryIcon name={cat.icon} size={22} />
                                    </span>
                                    <span className="hc-cat-card__body">
                                        <span className="hc-cat-card__title">{cat.title}</span>
                                        <span className="hc-cat-card__desc">{cat.short}</span>
                                    </span>
                                    <span className="hc-cat-card__meta">
                                        {cat.articleCount} article{cat.articleCount === 1 ? '' : 's'}
                                        <ChevronIcon size={16} />
                                    </span>
                                </Link>
                            ))}
                        </div>
                    )}
                    <Link className="hc-btn hc-btn--primary" to="/helpdesk/support/contact-trafficcreator-support">
                        Contact support
                    </Link>
                </div>
            )}

            {!hasQuery && (
                <p className="hc-ink-3-note">Start typing above to search the help center.</p>
            )}
        </section>
    );
};

export default SearchView;
